import type { RouteRecordRaw, RouteRecordSingleView } from 'vue-router'

/**
 * The Support workspace's route tree (2026-10-12, requesters-support-whatsapp.md §6).
 *
 * Every route carries `workspace: 'support'`, so the workspace guard turns away anyone the
 * session does not give the workspace to (a landlord, an officer, an administrator who is not
 * also a support officer), and a `support.*` capability the server also enforces on every
 * request. Two pages belong to other modules and are mounted here with this workspace's meta,
 * as the insights module mounts the notification centre: the tenancy page (read-only for
 * support) and the notification centre itself.
 *
 * **Entry weight.** This table is in the entry every marketplace visitor downloads, so it is
 * written to be small: the capability slugs are spelled here rather than imported from
 * `nav.ts` (which would bring the workspace's route builders into the entry with them --
 * `support-guards.spec.ts` asserts each one equals `CAPABILITIES`), and every component comes
 * through `pages/load.ts`, so the entry holds one small import and none of the preload lists
 * (the workspace's own pages are one lazy chunk, `pages/index.ts`).
 */
const load = () => import('./pages/load').then((m) => m.loaders)
const page = (name: keyof typeof import('./pages')) => () =>
  load()
    .then((loaders) => loaders.pages())
    .then((m) => m[name])
const tenancyPage = () => load().then((loaders) => loaders.tenancy())
const notificationsPage = () => load().then((loaders) => loaders.notifications())

/** A title key; a bare name is the workspace's own (`support.nav.<name>`). */
type Row = [
  path: string,
  name: string,
  component: RouteRecordSingleView['component'],
  capability: string,
  title: string,
]

const ROWS: Row[] = [
  ['', 'overview', page('SupportOverviewPage'), 'access', 'overview'],
  ['/requesters', 'requesters', page('SupportRequestersPage'), 'requester.view', 'requesters'],
  [
    '/requesters/:reference',
    'requester',
    page('SupportRequesterPage'),
    'requester.view',
    'requesters',
  ],
  [
    '/rent-follow-ups',
    'rent-follow-ups',
    page('SupportRentFollowUpsPage'),
    'tenancy.view',
    'rentFollowUps',
  ],
  [
    // The tenancy module's page, read-only in this workspace (it reads `meta.workspace`).
    '/rent-follow-ups/:id(\\d+)',
    'rent-follow-up',
    tenancyPage,
    'tenancy.view',
    'rentFollowUps',
  ],
  ['/officers', 'officers', page('SupportOfficersPage'), 'officer.view', 'officers'],
  ['/officers/:id', 'officer', page('SupportOfficerPage'), 'officer.view', 'officers'],
  ['/viewings', 'viewings', page('SupportViewingsPage'), 'viewing.view', 'viewings'],
  ['/viewings/:id', 'viewing', page('SupportViewingPage'), 'viewing.view', 'viewings'],
  // The Account pages carry the shared labels the sidebar's Account group shows them by.
  ['/notifications', 'notifications', notificationsPage, 'notification.view', 'nav.notifications'],
  ['/profile', 'profile', page('SupportProfilePage'), 'access', 'nav.profile'],
]

export const supportRoutes: RouteRecordRaw[] = ROWS.map(
  ([path, name, component, capability, title]) => ({
    path: `/app/support${path}`,
    name: `support-${name}`,
    component,
    meta: {
      layout: 'dashboard',
      requiresAuth: true,
      workspace: 'support',
      capability: `support.${capability}`,
      title: title.includes('.') ? title : `support.nav.${title}`,
    },
  }),
)
